import { TFunction } from 'i18next';
import { IntegrationConfigBase } from 'models/integrations/integrationConfigBase';
import { IIntegrationEvidence } from 'models/integrations/integrationEvidence';
import { toLocaleDateTime } from 'utils/datetime';

//
// Config class: the minimum restriction, the API's GuestInviteMinimumRestriction stored as its number
//
export enum GuestInviteMinimumRestriction {
  //admins and the Guest Inviter role, or no one
  AdminsAndGuestInviters = 0,
  //no one: an organization that never invites guests
  NoOne = 1,
}

export class IntegrationConfig_MS_GuestInviteRestriction extends IntegrationConfigBase {
  minimumRestriction: GuestInviteMinimumRestriction = GuestInviteMinimumRestriction.AdminsAndGuestInviters;
}

//The label of an allowInvitesFrom value. The API keeps the value as read (the Graph SDK name, AdminsAndGuestInviters),
//so the match ignores case; an unknown value is shown raw.
export const getGuestInviteValueText = (value: string | undefined, t: TFunction<string[]>): string => {
  if (value === undefined) return t('integrations:MS_GuestInviteRestriction.Result.Unknown');

  switch (value.toLowerCase()) {
    case 'none':
      return t('integrations:MS_GuestInviteRestriction.Values.None');
    case 'adminsandguestinviters':
      return t('integrations:MS_GuestInviteRestriction.Values.AdminsAndGuestInviters');
    case 'adminsguestinvitersandallmembers':
      return t('integrations:MS_GuestInviteRestriction.Values.AdminsGuestInvitersAndAllMembers');
    case 'everyone':
      return t('integrations:MS_GuestInviteRestriction.Values.Everyone');
    default:
      return value;
  }
};

export const getGuestInviteMinimumText = (minimum: GuestInviteMinimumRestriction, t: TFunction<string[]>): string => {
  return minimum === GuestInviteMinimumRestriction.NoOne
    ? t('integrations:MS_GuestInviteRestriction.Config.MinimumNoOne')
    : t('integrations:MS_GuestInviteRestriction.Config.MinimumAdminsAndGuestInviters');
};

export const getGuestInviteExpectedText = (minimum: GuestInviteMinimumRestriction, t: TFunction<string[]>): string => {
  return minimum === GuestInviteMinimumRestriction.NoOne
    ? t('integrations:MS_GuestInviteRestriction.Config.ExpectedNoOne')
    : t('integrations:MS_GuestInviteRestriction.Config.ExpectedAdminsAndGuestInviters');
};

//
// Evidence class: the one setting read, the values the minimum accepts, the outcome and when it was read
//
export class MS_GuestInviteRestrictionEvidence implements IIntegrationEvidence {
  isCapped: boolean = false;

  evaluatedAtUtc: Date = new Date();

  //undefined when Graph did not answer the property: shown as unknown, never as a pass
  allowInvitesFrom: string | undefined = undefined;

  //in the Graph wire spelling (adminsAndGuestInviters, none)
  acceptedValues: string[] = [];

  minimumRestriction: GuestInviteMinimumRestriction = GuestInviteMinimumRestriction.AdminsAndGuestInviters;

  pass: boolean = false;

  getFoundText(t: TFunction<string[]>): string {
    return getGuestInviteValueText(this.allowInvitesFrom, t);
  }

  toMarkdownReport(t: TFunction<['translation', 'integrations']>): string {
    const sb: string[] = [];

    sb.push(
      `## ${t('integrations:MS_GuestInviteRestriction.Report.SummaryHeading', {
        date: toLocaleDateTime(this.evaluatedAtUtc),
      })}`,
    );
    sb.push(
      `- ${t('integrations:MS_GuestInviteRestriction.Report.SettingLine', {
        found: this.getFoundText(t),
        expected: getGuestInviteExpectedText(this.minimumRestriction, t),
      })}`,
    );
    sb.push(
      `- ${t('integrations:MS_GuestInviteRestriction.Report.MinimumLine', {
        minimum: getGuestInviteMinimumText(this.minimumRestriction, t),
      })}`,
    );
    sb.push(
      `- ${t('integrations:MS_GuestInviteRestriction.Report.OutcomeLine', {
        outcome: this.pass
          ? t('integrations:MS_GuestInviteRestriction.Result.Pass')
          : t('integrations:MS_GuestInviteRestriction.Result.Fail'),
      })}`,
    );

    return sb.join('\n');
  }
}

export const parse_Evidence_MS_GuestInviteRestriction = (
  raw: string | undefined,
): MS_GuestInviteRestrictionEvidence | undefined => {
  if (!raw) return undefined;

  const rawEvidence = JSON.parse(raw);
  if (!rawEvidence) return undefined;

  const evidence = new MS_GuestInviteRestrictionEvidence();
  evidence.isCapped = rawEvidence.isCapped ?? false;
  evidence.evaluatedAtUtc = rawEvidence.evaluatedAtUtc ? new Date(rawEvidence.evaluatedAtUtc) : new Date();
  evidence.allowInvitesFrom =
    typeof rawEvidence.allowInvitesFrom === 'string' ? rawEvidence.allowInvitesFrom : undefined;
  evidence.acceptedValues = Array.isArray(rawEvidence.acceptedValues)
    ? rawEvidence.acceptedValues.filter((value: unknown) => typeof value === 'string')
    : [];
  evidence.minimumRestriction =
    rawEvidence.minimumRestriction === GuestInviteMinimumRestriction.NoOne
      ? GuestInviteMinimumRestriction.NoOne
      : GuestInviteMinimumRestriction.AdminsAndGuestInviters;
  evidence.pass = rawEvidence.pass ?? false;

  return evidence;
};
